/**
 * Diagnostics ring buffers for the "Copy diagnostics" button.
 *
 * Two ring buffers in localStorage, shared across same-origin iframes
 * (task pane + every dialog), so events emitted from the PDF dialog are
 * still in the packet when the user copies from the Settings tab in the
 * task pane.
 *
 * Buffer sizes are conservative; the whole packet should fit comfortably
 * in an email body even at the upper bound.
 */

import type { TelemetryEvent } from "./telemetry";

const TELEMETRY_KEY = "cogseven.companion.diagBuf.telemetry";
const ERROR_KEY = "cogseven.companion.diagBuf.errors";

const MAX_TELEMETRY = 50;
const MAX_ERRORS = 20;

export interface BufferedTelemetry extends TelemetryEvent {
  at: string;
}

export interface BufferedError {
  at: string;
  error_code: string;
  error_message_masked: string;
  stack_summary?: string;
}

function readBuffer<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeBuffer<T>(key: string, items: T[], maxSize: number): void {
  try {
    const trimmed = items.length > maxSize ? items.slice(-maxSize) : items;
    localStorage.setItem(key, JSON.stringify(trimmed));
  } catch {
    // Storage may be full or unavailable. Best effort: shrink to half size.
    try {
      const half = Math.max(1, Math.floor(maxSize / 2));
      localStorage.setItem(key, JSON.stringify(items.slice(-half)));
    } catch {
      // Give up silently; diagnostics buffer is a best-effort feature.
    }
  }
}

export function pushTelemetry(event: TelemetryEvent): void {
  const buf = readBuffer<BufferedTelemetry>(TELEMETRY_KEY);
  buf.push({ ...event, at: new Date().toISOString() });
  writeBuffer(TELEMETRY_KEY, buf, MAX_TELEMETRY);
}

export function pushError(err: Omit<BufferedError, "at">): void {
  const buf = readBuffer<BufferedError>(ERROR_KEY);
  buf.push({ ...err, at: new Date().toISOString() });
  writeBuffer(ERROR_KEY, buf, MAX_ERRORS);
}

export function getRecentTelemetry(): BufferedTelemetry[] {
  return readBuffer<BufferedTelemetry>(TELEMETRY_KEY);
}

export function getRecentErrors(): BufferedError[] {
  return readBuffer<BufferedError>(ERROR_KEY);
}

export function clearBuffers(): void {
  try {
    localStorage.removeItem(TELEMETRY_KEY);
    localStorage.removeItem(ERROR_KEY);
  } catch {
    // ignore
  }
}
